// Columna central del explorer: las 6 columnas del diseño, pintadas con el `DataTable` compartido.
// El mock no ordena por encabezado: el orden es fijo (día prometido descendente) y lo pone `logic`.
import { cn } from '@/lib/cn';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    formatPromiseDay,
    formatPromiseMoney,
    PROMISE_CHANNEL_CHIP_LABEL,
    PROMISE_CHANNEL_DESCRIPTION,
    PROMISE_CHANNEL_TONE,
    PROMISE_STATUS_LABEL,
    PROMISE_STATUS_TONE,
    TONE_CHIP_CLASS,
    type PromiseTone,
} from '../format';
import type { PaymentPromiseRow } from '../types';

/** Chip relleno de celda (`.chip` del mock a 9px): lo usan Canal y Estado. */
export function PromiseChip({
    tone,
    title,
    children,
}: {
    tone: PromiseTone;
    title?: string;
    children: string;
}) {
    return (
        <span
            title={title}
            className={cn(
                'inline-flex items-center whitespace-nowrap rounded-full border px-[5px] py-px text-[9px] font-medium',
                TONE_CHIP_CLASS[tone],
            )}
        >
            {children}
        </span>
    );
}

/** El chip de Canal con la descripción larga del diseño (M4 §4.5) en su `title`. */
export function ChannelChip({ channel }: { channel: PaymentPromiseRow['channel'] }) {
    return (
        <PromiseChip
            tone={PROMISE_CHANNEL_TONE[channel]}
            title={PROMISE_CHANNEL_DESCRIPTION[channel]}
        >
            {PROMISE_CHANNEL_CHIP_LABEL[channel]}
        </PromiseChip>
    );
}

export function StatusChip({ status }: { status: PaymentPromiseRow['status'] }) {
    return (
        <PromiseChip tone={PROMISE_STATUS_TONE[status]}>{PROMISE_STATUS_LABEL[status]}</PromiseChip>
    );
}

/** Las 6 columnas del diseño con SUS anchos. Cliente es la flexible y se queda el resto. */
const COLUMNS: DataTableColumn<PaymentPromiseRow>[] = [
    {
        key: 'promise',
        label: 'PR',
        width: '78px',
        // eslint-disable-next-line no-restricted-syntax -- columna «PR»: el folio del compromiso es el dato, no un id que se resuelva a nombre
        cell: (row) => <span className="font-mono text-[10.5px]">{row.promiseId}</span>,
    },
    {
        key: 'customer',
        label: 'Cliente',
        cell: (row) => row.customerName,
    },
    {
        key: 'day',
        label: 'Fecha',
        width: '78px',
        cell: (row) => (
            <span className="text-[10.5px] tabular-nums">{formatPromiseDay(row.promisedDay)}</span>
        ),
    },
    {
        key: 'amount',
        label: 'Monto',
        width: '74px',
        numeric: true,
        cell: (row) => (
            <span className="font-semibold tabular-nums">
                {formatPromiseMoney(row.promisedAmountCents)}
            </span>
        ),
    },
    {
        key: 'channel',
        label: 'Canal',
        width: '70px',
        cellOverflow: 'nowrap',
        cell: (row) => <ChannelChip channel={row.channel} />,
    },
    {
        key: 'status',
        label: 'Estado',
        width: '86px',
        cellOverflow: 'nowrap',
        cell: (row) => <StatusChip status={row.status} />,
    },
];

/** Las 6 columnas suman 386px fijos y Cliente pide su espacio: por debajo la tabla scrollea. */
const TABLE_MIN_WIDTH = '560px';

export interface PromisesTableProps {
    /** Filas YA filtradas y ordenadas. */
    rows: readonly PaymentPromiseRow[];
    selectedPromiseId: string | null;
    onSelect: (promiseId: string) => void;
    className?: string;
}

export function PromisesTable({
    rows,
    selectedPromiseId,
    onSelect,
    className,
}: PromisesTableProps) {
    return (
        <DataTable
            columns={COLUMNS}
            rows={rows}
            rowKey={(row) => row.promiseId}
            minWidth={TABLE_MIN_WIDTH}
            caption="Compromisos de pago del cobrador, con su fecha prometida, monto, canal y estado."
            isRowSelected={(row) => row.promiseId === selectedPromiseId}
            onRowClick={(row) => {
                onSelect(row.promiseId);
            }}
            empty="Ningún compromiso coincide con los filtros."
            className={className}
        />
    );
}
